<template>
  <b-modal
    :visible="!!invoice"
    title="Add Payment"
    size="md"
    no-close-on-backdrop
    @hidden="$emit('close')"
  >
    <template v-if="invoice">
      <div class="ipm-summary">
        <div><span class="ipm-label">Invoice</span> <span class="ipm-mono">{{ invoice.invoice_reference }}</span></div>
        <div><span class="ipm-label">Invoice amount</span> {{ formatCurrency(invoice.amount, invoice.currency_type) }}</div>
        <div><span class="ipm-label">Payments</span> {{ formatCurrency(invoice.amount_paid, invoice.currency_type) }}</div>
        <div>
          <span class="ipm-label">Remaining balance</span>
          <strong :class="invoice.remaining_balance > 0 ? 'ipm-owing' : 'ipm-settled'">
            {{ formatCurrency(invoice.remaining_balance, invoice.currency_type) }}
          </strong>
        </div>
      </div>

      <b-form @submit.prevent="submit">
        <b-form-group label="Amount" label-for="ipm-amount" label-size="sm">
          <b-form-input
            id="ipm-amount"
            v-model="amount"
            type="number"
            step="0.01"
            min="0.01"
            :max="invoice.remaining_balance"
            size="sm"
            required
          />
          <b-link class="ipm-fill" @click="amount = String(invoice.remaining_balance)">
            Pay the full remaining balance
          </b-link>
        </b-form-group>

        <b-form-group label="Payment date" label-for="ipm-date" label-size="sm">
          <b-form-input id="ipm-date" v-model="paymentDate" type="date" :max="today" size="sm" required />
        </b-form-group>

        <b-form-group label="Reference (optional)" label-for="ipm-reference" label-size="sm">
          <b-form-input id="ipm-reference" v-model="reference" maxlength="255" size="sm" />
        </b-form-group>

        <b-alert :show="!!error" variant="danger" class="ipm-error">{{ error }}</b-alert>
      </b-form>

      <div class="ipm-history">
        <div class="ipm-history__title">Payments recorded</div>
        <div v-if="loadingPayments" class="ipm-muted">Loading…</div>
        <div v-else-if="!payments.length" class="ipm-muted">No payments recorded yet.</div>
        <table v-else class="ipm-history__table">
          <thead>
            <tr><th>Date</th><th>Amount</th><th>Reference</th><th>Recorded by</th></tr>
          </thead>
          <tbody>
            <tr v-for="p in payments" :key="p.id">
              <td>{{ p.payment_date }}</td>
              <td>{{ formatCurrency(p.amount, invoice.currency_type) }}</td>
              <td>{{ p.reference || '—' }}</td>
              <td>{{ p.recorded_by }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template #modal-footer="{ cancel }">
      <b-button size="sm" variant="outline-secondary" :disabled="saving" @click="cancel()">Cancel</b-button>
      <b-button size="sm" variant="primary" :disabled="saving || !canSubmit" @click="submit">
        <b-spinner v-if="saving" small class="mr-1" />
        Add Payment
      </b-button>
    </template>
  </b-modal>
</template>

<script>
// Superuser-only, one invoice at a time (LIQ-2073/2077) — InvoiceViewTable
// only opens this for a single selected invoice, and the proxy refuses
// anyone else. Lincoln re-checks the balance on submit, so an amount over
// the remaining balance is refused there even if it gets past the form.
import { defineComponent, ref, computed, watch } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import { formatCurrency } from './useInvoiceExport'
import { requestErrorMessage, responseBody } from './useInvoicingSelection'

const todayIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default defineComponent({
  name: 'InvoicePaymentModal',
  props: {
    // The invoice row (as listed in the Invoice view), or null when closed.
    invoice: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const amount = ref('')
    const paymentDate = ref(todayIso())
    const reference = ref('')
    const saving = ref(false)
    const error = ref('')
    const payments = ref([])
    const loadingPayments = ref(false)
    const today = todayIso()

    const loadPayments = async (invoiceId) => {
      loadingPayments.value = true
      try {
        const resp = await InvoicingService.getInvoicePayments(invoiceId)
        payments.value = responseBody(resp).data || []
      } catch (e) {
        payments.value = []
      } finally {
        loadingPayments.value = false
      }
    }

    watch(() => props.invoice, (invoice) => {
      amount.value = ''
      paymentDate.value = todayIso()
      reference.value = ''
      error.value = ''
      payments.value = []
      if (invoice) loadPayments(invoice.invoice_id)
    }, { immediate: true })

    const canSubmit = computed(() => {
      const value = Number(amount.value)
      return !!props.invoice && value > 0 && value <= Number(props.invoice.remaining_balance) && !!paymentDate.value
    })

    const submit = async () => {
      if (!canSubmit.value || saving.value) return
      saving.value = true
      error.value = ''
      try {
        await InvoicingService.addInvoicePayment({
          invoice_id: props.invoice.invoice_id,
          amount: Number(amount.value).toFixed(2),
          payment_date: paymentDate.value,
          reference: reference.value,
        })
        emit('saved')
      } catch (e) {
        error.value = await requestErrorMessage(e, 'The payment could not be saved. Please try again.')
      } finally {
        saving.value = false
      }
    }

    return {
      amount, paymentDate, reference, saving, error, payments, loadingPayments, today,
      canSubmit, submit, formatCurrency,
    }
  },
})
</script>

<style scoped>
.ipm-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  font-size: 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f4f6f8;
  border-radius: 6px;
}
.ipm-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #8898aa; }
.ipm-mono { font-family: monospace; }
.ipm-owing { color: #f5365c; }
.ipm-settled { color: #2dce89; }
.ipm-fill { font-size: 11px; }
.ipm-error { font-size: 12px; padding: 6px 10px; }
.ipm-history { margin-top: 10px; border-top: 1px solid #e9ecef; padding-top: 10px; }
.ipm-history__title { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #32325d; margin-bottom: 6px; }
.ipm-history__table { width: 100%; font-size: 11px; }
.ipm-history__table th { font-size: 10px; color: #8898aa; font-weight: 700; text-transform: uppercase; padding: 4px 6px; }
.ipm-history__table td { padding: 4px 6px; border-top: 1px solid #f0f0f0; }
.ipm-muted { font-size: 11px; color: #8898aa; }
</style>
